import OpenAI from 'openai';
import { AVAILABLE_MODELS, anthropic, ModelName } from '../utils/modelConfig';

export interface Idea {
  id: string;
  content: string;
  children: Idea[];
  parent_id: string | null;
  position: number;
  context?: string;
}

const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY;

if (!OPENAI_API_KEY) {
  throw new Error('REACT_APP_OPENAI_API_KEY is not defined in environment variables');
}

const openaiInstance = new OpenAI({
  apiKey: OPENAI_API_KEY,
  dangerouslyAllowBrowser: true // Required for client-side usage
});

export abstract class ArtifactGenerator {
  protected ideas: Idea[];
  protected modelName: ModelName;

  constructor(ideas: Idea[], modelName?: ModelName) {
    this.ideas = ideas;
    this.modelName = modelName || 'gpt-4o-mini';
  }

  abstract generatePrompt(): string;

  async generate(): Promise<string> {
    try {
      const prompt = this.generatePrompt();
      const modelConfig = AVAILABLE_MODELS[this.modelName];

      if (modelConfig.provider === 'openai') {
        const completion = await openaiInstance.chat.completions.create({
          model: modelConfig.name,
          messages: [
            {
              role: "system",
              content: "You are a professional technical writer and product strategist who excels at creating various types of product documentation and artifacts. Your output should be in markdown format."
            },
            {
              role: "user",
              content: prompt
            }
          ],
          temperature: modelConfig.temperature,
          max_tokens: modelConfig.maxTokens
        });

        const content = completion.choices[0]?.message?.content;
        if (!content) {
          throw new Error('No content generated');
        }
        return content;

      } else if (modelConfig.provider === 'anthropic') {
        const message = await anthropic.messages.create({
          model: modelConfig.name,
          max_tokens: modelConfig.maxTokens,
          temperature: modelConfig.temperature,
          system: "You are a professional technical writer and product strategist who excels at creating various types of product documentation and artifacts. Your output should be in markdown format.",
          messages: [
            {
              role: "user",
              content: prompt
            }
          ]
        });

        const content = message.content[0]?.text;
        if (!content) {
          throw new Error('No content generated');
        }
        return content;
      }

      throw new Error(`Unsupported model provider: ${modelConfig.provider}`);
    } catch (error) {
      console.error('AI API error:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to generate content');
    }
  }

  protected getFormattedContent(): string {
    return this.ideas.map(idea => this.formatIdea(idea)).join('\n\n');
  }

  protected getIdeaHierarchy(): string {
    return this.ideas.map(idea => {
      const contextPrefix = idea.context ? `[Context: ${idea.context}]\n` : '';
      return contextPrefix + this.formatIdeaHierarchy(idea);
    }).join('\n\n');
  }

  protected formatIdea(idea: Idea, level: number = 0): string {
    const indent = '  '.repeat(level);
    const contextPrefix = idea.context ? `[Context: ${idea.context}]\n${indent}` : '';
    let content = `${contextPrefix}${idea.content}`;
    
    if (idea.children && idea.children.length > 0) {
      content += '\n' + idea.children
        .map(child => this.formatIdea(child, level + 1))
        .join('\n');
    }
    
    return content;
  }

  protected formatIdeaHierarchy(idea: Idea, level: number = 0): string {
    const indent = '  '.repeat(level);
    let content = `${indent}- ${idea.content}`;
    
    if (idea.children && idea.children.length > 0) {
      content += '\n' + idea.children
        .map(child => this.formatIdeaHierarchy(child, level + 1))
        .join('\n');
    }
    
    return content;
  }
}
